import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import apiRequest from "../../lib/http"

export const fetchCompanyById = createAsyncThunk('company/fetchCompanyById', async (companyId, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get(`/company/${companyId}`)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch company'
    return rejectWithValue(errorMessage)
  }
})

export const updateCompany = createAsyncThunk('company/updateCompany', async ({ companyId, companyData }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put(`/company/${companyId}`, companyData)
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to update company'
    return rejectWithValue(errorMessage)
  }
})

export const fetchAllCompanies = createAsyncThunk('company/fetchAllCompanies', async (_, { rejectWithValue }) => {
  try {
    const response = await apiRequest.get('/company')
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to fetch companies'
    return rejectWithValue(errorMessage)
  }
})

// The first-N pair travels on EVERY save. The endpoint reads an omitted pair as an instruction
// to clear the window, so dropping the fields here silently erased a configured first-N window
// on any unrelated settings save (WS 1241875).
// cardBackupGateway travels on every save for the same reason: the PUT replaces the whole
// paymentDefaults subdocument, so a save that never mentions the card failover destination
// erases one that was set out-of-band. Defaulted to null (= "no backup"), never omitted.
// defaultProcessingMode travels only when the caller states it — the endpoint treats an
// omitted mode as "leave the stored value untouched", never as a reset to 'review'.
export const updatePaymentSettings = createAsyncThunk('company/updatePaymentSettings', async ({ achDefaultGateway, cardDefaultGateway, cardBackupGateway = null, achFirstNCount = null, achFirstNGateway = null, defaultProcessingMode }, { rejectWithValue }) => {
  try {
    const response = await apiRequest.put('/company/payment-settings', {
      achDefaultGateway, cardDefaultGateway, cardBackupGateway, achFirstNCount, achFirstNGateway,
      ...(defaultProcessingMode !== undefined ? { defaultProcessingMode } : {}),
    })
    return response.data
  } catch (error) {
    const errorMessage = error.response?.data?.error?.errors?.[0]?.message || 'Failed to update payment settings'
    return rejectWithValue(errorMessage)
  }
})

const initialState = {
  paymentDefaults: null,
  defaultProcessingMode: null,
  loading: false,
  error: null,
}

// Every fulfilled company payload carries the full company doc, so the three write/read
// thunks below store the same slice fields the same way.
const storeCompanyFields = (state, action) => {
  state.paymentDefaults = action.payload.data?.paymentDefaults ?? state.paymentDefaults
  state.defaultProcessingMode = action.payload.data?.defaultProcessingMode ?? state.defaultProcessingMode
}

const companySlice = createSlice({
  name: 'company',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCompanyById.fulfilled, (state, action) => {
        state.loading = false
        storeCompanyFields(state, action)
      })
      .addCase(fetchCompanyById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updateCompany.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCompany.fulfilled, (state, action) => {
        state.loading = false
        storeCompanyFields(state, action)
      })
      .addCase(updateCompany.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
      .addCase(updatePaymentSettings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePaymentSettings.fulfilled, (state, action) => {
        state.loading = false
        storeCompanyFields(state, action)
      })
      .addCase(updatePaymentSettings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload
      })
  },
})

export default companySlice.reducer
